import { Component, OnDestroy } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Subscription, Observable } from 'rxjs';
import { startWith, filter, distinctUntilChanged, map } from 'rxjs/operators';
import { ReusableStreamProperty } from '@ipreo/muni-shared';
import { calculateSummary } from './deal-summary-card.calculations';
import { GameDayRootReducerState } from '../../../state/root.state';
import { MONITOR } from '../../gameday.constants';
import { DealSummary } from '../../models/deal-summary';
import { getTrancheOrdersFilteredState } from '../../../state/data/reducer';

const DEAL_SUMMARY_INIT_STATE: DealSummary = {
  balance: undefined,
  subscription: undefined,
  amount: undefined,
  fill: undefined,
  fillPercent: undefined,
  shortPercent: undefined,
  midPercent: undefined,
  longPercent: undefined
};

@Component({
  selector: 'muni-gameday-deal-summary-card',
  templateUrl: './deal-summary-card.template.html',
  styleUrls: ['./deal-summary-card.styles.scss']
})
export class DealSummaryCardComponent implements OnDestroy {
  public readonly monitorUrl: string = MONITOR;
  public summary: DealSummary;

  @ReusableStreamProperty
  public readonly summary$: Observable<DealSummary> = this.store.pipe(
    select(getTrancheOrdersFilteredState),
    filter(({ deal }) => !!deal),
    distinctUntilChanged(),
    map(calculateSummary)
  );

  public readonly summaryWithInitState: Observable<
    DealSummary
  > = this.summary$.pipe(startWith(DEAL_SUMMARY_INIT_STATE));

  public readonly dataSubscription: Subscription = this.summaryWithInitState.subscribe(
    total => (this.summary = total)
  );

  constructor(private store: Store<GameDayRootReducerState>) {}

  public ngOnDestroy(): void {
    this.dataSubscription.unsubscribe();
  }

  public subscriptionFormatter(d: number): string | number {
    return d
      ? d < 1
        ? `(${(d * 100).toFixed(0)}%)`
        : `(${d.toFixed(1)}x)`
      : d;
  }

  public get hasShort(): boolean {
    return this.summary && this.summary.shortPercent !== undefined;
  }

  public get hasMid(): boolean {
    return this.summary && this.summary.midPercent !== undefined;
  }

  public get hasLong(): boolean {
    return this.summary && this.summary.longPercent !== undefined;
  }

  public get columnClassName(): string {
    return this.threeBars ? 'columnTriple' : 'column';
  }

  public get threeBars(): boolean {
    return this.hasShort && this.hasMid && this.hasLong;
  }

  public get oneBar(): boolean {
    return (
      (this.hasShort && !this.hasMid && !this.hasLong) ||
      (!this.hasShort && this.hasMid && !this.hasLong) ||
      (!this.hasShort && !this.hasMid && this.hasLong)
    );
  }
}
